import React, { FC, useMemo } from 'react';
import { Flex } from '@components/Flex';
import { getRootUrl } from '@utils/urlHelpers.util';
import {
  PreviousSlideButton,
  NextSlideButton,
  StyledCTALink,
  StyledContainer,
  StyledTextPairing,
  StyledCarousel,
} from './RecommendedProducts.styles';
import { GridView, productPhotoSizes, RecommendProducts, StoreFront } from '@sections/shared/GridView';
import { JoykitIcon } from '@components/JoykitIcon';
import { CurrencyProvider } from '@utils/currency';
import { useTranslation } from 'react-i18next';

interface RecommendedProductsProps {
  products: RecommendProducts;
}

/**
 * Desktop slot for /registry/, measured in the browser rather than derived from the theme: the 3-up
 * grid renders a ~157px tile at any viewport at or above the `sm` breakpoint (981 and 1300 both
 * measure 157–159, so it does not scale with the viewport).
 *
 * The shared default claims 280px, which makes a 2x screen ask for 560 and take `medium` (960x960,
 * 183 KiB). Declaring 200px keeps a 2x screen at 400 so it takes `small` (480x480, 47.5 KiB), with
 * headroom over the measured 157. `productPhotoSizes` supplies the phone and pinned-tile stops.
 */
const REGISTRY_PRODUCT_PHOTO_SIZES = productPhotoSizes('200px');

export const RecommendedProducts: FC<RecommendedProductsProps> = ({ products }) => {
  const formattedProduct = useMemo(() => {
    const productData = [...products].filter((data) => !!data.node.catalogItemGroupData?.tileViewItem);
    productData.forEach((product) => {
      product.node.catalogItemGroupData.tileViewItem.publicUrlPath =
        product.node.catalogItemGroupData?.tileViewItem.brand.publicUrlPath || '';
    });
    return productData;
  }, [products]);

  const { t } = useTranslation('registry');
  const shopSection = t('shopSection');

  return (
    <StyledContainer>
      <CurrencyProvider>
        <StyledCarousel
          wrapAround
          renderCenterLeftControls={({ previousSlide }) => (
            <PreviousSlideButton aria-label="Previous Slide Button" onClick={previousSlide} id="registry-shop-prev-btn">
              <JoykitIcon name="backChevron" color="mono1" size={24} />
            </PreviousSlideButton>
          )}
          renderCenterRightControls={({ nextSlide }) => (
            <NextSlideButton aria-label="Next Slide Button" onClick={nextSlide} id="regsitry-shop-next-btn">
              <JoykitIcon name="forwardChevron" color="mono1" size={24} />
            </NextSlideButton>
          )}
          renderBottomCenterControls={null}
        >
          <GridView
            items={formattedProduct?.slice(0, 6)}
            itemPerLine={{ sm: 3, belowSm: 2 }}
            eventName="RegistryProductClicked"
            storeFront={StoreFront.wedding}
            enabledAmplitude={true}
            displayPrice={true}
            photoSizes={REGISTRY_PRODUCT_PHOTO_SIZES}
          />
          <GridView
            items={formattedProduct?.slice(6, 12)}
            itemPerLine={{ sm: 3, belowSm: 2 }}
            eventName="RegistryProductClicked"
            storeFront={StoreFront.wedding}
            enabledAmplitude={true}
            displayPrice={true}
            photoSizes={REGISTRY_PRODUCT_PHOTO_SIZES}
          />
        </StyledCarousel>
      </CurrencyProvider>

      <Flex flexDirection="column" alignItems="center" paddingTop={6}>
        <StyledTextPairing
          titleVariant="calloutHed40"
          titleColor="neutra6"
          titleTagName="h3"
          title={shopSection.productSectionTitle}
          body={shopSection.productSectionSubTitle}
          bodyVariant="dek20"
          bodyColor="mono1"
          bodyMaxWidth="480px"
          marginBottom={[6, 9]}
          textAlign="center"
        />
        <Flex justifyContent="center" style={{ width: '100%' }}>
          <StyledCTALink id="shopProductsCTA" href={getRootUrl('/shop')} variant="primary" typography="featureHed17" hasChevron>
            {shopSection.productSectionCTA}
          </StyledCTALink>
        </Flex>
      </Flex>
    </StyledContainer>
  );
};
